import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Loader from '@amzn/meridian/loader';
import Alert from '@amzn/meridian/alert';
import Table, { TableRow, TableCell } from '@amzn/meridian/table';
import { adminApi, TemplateRecord } from '../service/adminApiService';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../components/ToastProvider';
import { PageHeader } from '../components/PageHeader';

export const TemplatesListPage: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [templates, setTemplates] = useState<TemplateRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<TemplateRecord | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await adminApi.listTemplates();
      setTemplates(resp.templates);
    } catch (err: any) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const performDelete = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await adminApi.deleteTemplate(pending.template_name);
      toast.push({ type: 'success', message: `Deleted template ${pending.template_name}` });
      setPending(null);
      refresh();
    } catch (err: any) {
      toast.push({ type: 'error', message: err?.message || 'Delete failed' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Column spacing="large">
      <PageHeader
        title="Templates"
        subtitle="Templates pre-fill the allowed-operations checkboxes on the group form. System templates can be edited but not deleted."
        actions={
          <Button type="primary" onClick={() => navigate('/admin/templates/new')}>
            + New template
          </Button>
        }
      />

      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <Row alignmentHorizontal="center" spacingInset="500">
          <Loader type="circular" size="large" />
        </Row>
      ) : templates.length === 0 ? (
        <Alert type="neutral">No templates defined.</Alert>
      ) : (
        <Table headerRows={1} showDividers={true} aria-label="Templates">
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Description</TableCell>
            <TableCell>Operations</TableCell>
            <TableCell>System</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
          {templates.map((t) => (
            <TableRow key={t.template_name}>
              <TableCell>
                <Link to={`/admin/templates/${encodeURIComponent(t.template_name)}/edit`}>
                  {t.template_name}
                </Link>
              </TableCell>
              <TableCell>
                <Text type="b200">{t.description || '-'}</Text>
              </TableCell>
              <TableCell>
                <Text type="b200">
                  {t.operations.length} op(s): {t.operations.slice(0, 4).join(', ')}
                  {t.operations.length > 4 ? '…' : ''}
                </Text>
              </TableCell>
              <TableCell>{t.is_system ? 'Yes' : 'No'}</TableCell>
              <TableCell>
                <Row spacing="xsmall">
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() =>
                      navigate(`/admin/templates/${encodeURIComponent(t.template_name)}/edit`)
                    }
                  >
                    Edit
                  </Button>
                  <Button
                    type="tertiary"
                    size="small"
                    onClick={() => setPending(t)}
                    disabled={t.is_system}
                  >
                    Delete
                  </Button>
                </Row>
              </TableCell>
            </TableRow>
          ))}
        </Table>
      )}

      <ConfirmDialog
        open={!!pending}
        title={`Delete template ${pending?.template_name || ''}?`}
        message="This removes the template from the group form's picker. Existing groups are unaffected."
        confirmLabel="Delete"
        destructive
        busy={busy}
        onConfirm={performDelete}
        onCancel={() => setPending(null)}
      />
    </Column>
  );
};
